/**
 * PATH: packages/email-hub/src/feature/components/charts/ChartDataEditor.tsx
 * FILE_ID: EMAILHUB-CHARTS-DATAEDITOR-001
 * PURPOSE: Compact editable table for chart data — one label column plus
 *          N series columns, add/remove rows & series.
 * WHY: The manual entry path of FEAT-2026-07-03-COMPOSE-CHARTS. Emits plain
 *      draft rows; the modal runs them through parseChartSpec so manual
 *      input obeys the same whitelist as AI output.
 * RELATED:
 *   - ComposeChartModal.tsx: owns the draft state this edits
 */
import { Plus, X } from 'lucide-react'

export interface ChartDraftRow {
  label: string
  /** seriesName → raw text (numbers validated by parseChartSpec). */
  values: Record<string, string>
}

export interface ChartDraft {
  seriesNames: string[]
  rows: ChartDraftRow[]
}

export function emptyChartDraft(): ChartDraft {
  return {
    seriesNames: ['Value'],
    rows: [
      { label: 'Jan', values: { Value: '10' } },
      { label: 'Feb', values: { Value: '20' } },
      { label: 'Mar', values: { Value: '15' } },
    ],
  }
}

const inputCls =
  'w-full rounded border border-border bg-background px-1.5 py-1 text-xs text-foreground focus:border-primary focus:outline-none'

export function ChartDataEditor({
  draft,
  onChange,
}: {
  draft: ChartDraft
  onChange: (next: ChartDraft) => void
}) {
  const renameSeries = (idx: number, name: string) => {
    const old = draft.seriesNames[idx]
    const seriesNames = draft.seriesNames.map((s, i) => (i === idx ? name : s))
    const rows = draft.rows.map((r) => {
      const values = { ...r.values }
      if (old in values) { values[name] = values[old]; delete values[old] }
      return { ...r, values }
    })
    onChange({ seriesNames, rows })
  }
  const addSeries = () => {
    if (draft.seriesNames.length >= 8) return
    let n = draft.seriesNames.length + 1
    while (draft.seriesNames.includes(`Series ${n}`)) n++
    onChange({ ...draft, seriesNames: [...draft.seriesNames, `Series ${n}`] })
  }
  const removeSeries = (idx: number) => {
    if (draft.seriesNames.length <= 1) return
    const name = draft.seriesNames[idx]
    onChange({
      seriesNames: draft.seriesNames.filter((_, i) => i !== idx),
      rows: draft.rows.map((r) => {
        const values = { ...r.values }
        delete values[name]
        return { ...r, values }
      }),
    })
  }
  const setCell = (rowIdx: number, series: string | null, value: string) => {
    onChange({
      ...draft,
      rows: draft.rows.map((r, i) => {
        if (i !== rowIdx) return r
        if (series === null) return { ...r, label: value }
        return { ...r, values: { ...r.values, [series]: value } }
      }),
    })
  }
  const addRow = () => {
    if (draft.rows.length >= 60) return
    onChange({ ...draft, rows: [...draft.rows, { label: '', values: {} }] })
  }
  const removeRow = (idx: number) => {
    onChange({ ...draft, rows: draft.rows.filter((_, i) => i !== idx) })
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid="chart-data-editor">
      <table className="w-full border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="text-left text-[11px] font-medium text-muted-foreground">Label</th>
            {draft.seriesNames.map((name, i) => (
              <th key={i} className="text-left">
                <div className="flex items-center gap-1">
                  <input
                    value={name}
                    aria-label={`Series ${i + 1} name`}
                    onChange={(e) => renameSeries(i, e.target.value)}
                    className={`${inputCls} font-medium`}
                  />
                  {draft.seriesNames.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove series ${name}`}
                      onClick={() => removeSeries(i)}
                      className="rounded p-0.5 text-muted-foreground hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </th>
            ))}
            <th className="w-6" />
          </tr>
        </thead>
        <tbody>
          {draft.rows.map((row, r) => (
            <tr key={r}>
              <td>
                <input
                  value={row.label}
                  aria-label={`Row ${r + 1} label`}
                  placeholder="Label"
                  onChange={(e) => setCell(r, null, e.target.value)}
                  className={inputCls}
                />
              </td>
              {draft.seriesNames.map((name) => (
                <td key={name}>
                  <input
                    value={row.values[name] ?? ''}
                    aria-label={`${name} value for row ${r + 1}`}
                    placeholder="0"
                    inputMode="decimal"
                    onChange={(e) => setCell(r, name, e.target.value)}
                    className={inputCls}
                  />
                </td>
              ))}
              <td>
                <button
                  type="button"
                  aria-label={`Remove row ${r + 1}`}
                  onClick={() => removeRow(r)}
                  className="rounded p-0.5 text-muted-foreground hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
        >
          <Plus className="h-3 w-3" /> Row
        </button>
        <button
          type="button"
          onClick={addSeries}
          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
        >
          <Plus className="h-3 w-3" /> Series
        </button>
      </div>
    </div>
  )
}
